import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';

// Type definitions for Google Maps API
interface GoogleMapsConfig {
    v: string;
    key: string;
}

interface GoogleMapsLoader {
    (config: GoogleMapsConfig): void;
}

interface GoogleMapsAPI {
    maps: {
        importLibrary: (library: string) => Promise<any>;
        Map: any;
        Marker: any;
        InfoWindow: any;
        PlacesService: any;
        AdvancedMarkerElement: any;
        [key: string]: any;
    };
}

// Library type mappings for better type safety
interface LibraryMap {
    core: {
        Map: any;
        LatLng: any;
        LatLngBounds: any;
        Point: any;
        Size: any;
    };
    maps: {
        Map: any;
        InfoWindow: any;
        Polyline: any;
        Polygon: any;
        Circle: any;
        Rectangle: any;
    };
    places: {
        PlacesService: any;
        PlacesServiceStatus: any;
        AutocompleteService: any;
        Autocomplete: any;
        PlaceAutocompleteElement: any;
    };
    marker: {
        Marker: any;
        AdvancedMarkerElement: any;
        PinElement: any;
    };
    geometry: {
        spherical: any;
        encoding: any;
        poly: any;
    };
    geocoding: {
        Geocoder: any;
        GeocoderStatus: any;
    };
    streetView: {
        StreetViewPanorama: any;
        StreetViewService: any;
    };
    drawing: {
        DrawingManager: any;
        OverlayType: any;
    };
    visualization: {
        HeatmapLayer: any;
    };
}

type LibraryName = keyof LibraryMap;

declare global {
    interface Window {
        google: GoogleMapsAPI;
    }
}

@Injectable({
    providedIn: 'root',
})
export class MapLoaderService {
    private scriptLoaded = false;
    private loadPromise: Promise<void> | null = null;

    load(): Promise<void> {
        if (this.scriptLoaded) {
            return Promise.resolve();
        }

        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = new Promise<void>((resolve, reject) => {
            try {
                // Type-safe Google Maps JavaScript API loader script
                const loader: GoogleMapsLoader = (g: GoogleMapsConfig) => {
                    let h: Promise<void> | null = null;
                    let a: HTMLScriptElement;
                    let k: keyof GoogleMapsConfig;

                    const p = 'The Google Maps JavaScript API';
                    const c = 'google';
                    const l = 'importLibrary';
                    const q = '__ib__';
                    const m = document;
                    const b = window as any;

                    b[c] = b[c] || {};
                    const d = b[c].maps || (b[c].maps = {});
                    const r = new Set<string>();
                    const e = new URLSearchParams();

                    const u = (): Promise<void> =>
                        h ||
                        (h = new Promise(async (f, n) => {
                            a = m.createElement('script');
                            e.set('libraries', [...r].join(','));

                            for (k in g) {
                                const key = k.replace(
                                    /[A-Z]/g,
                                    (t) => '_' + t[0].toLowerCase()
                                );
                                e.set(key, g[k]);
                            }

                            e.set('callback', c + '.maps.' + q);
                            a.src =
                                `https://maps.${c}apis.com/maps/api/js?` +
                                e.toString();

                            d[q] = f;
                            a.onerror = () =>
                                (h = Promise.reject(
                                    new Error(p + ' could not load.')
                                ));

                            const nonceScript = m.querySelector(
                                'script[nonce]'
                            ) as HTMLScriptElement;
                            a.nonce = nonceScript?.nonce || '';

                            m.head.appendChild(a);
                        }));

                    if (d[l]) {
                        console.warn(p + ' only loads once. Ignoring:', g);
                    } else {
                        d[l] = (f: string, ...n: any[]) => {
                            r.add(f);
                            return u().then(() => d[l](f, ...n));
                        };
                    }
                };

                // Initialize the loader
                loader({
                    v: 'weekly',
                    key: environment.googleMapsApiKey,
                });

                // Type-safe polling mechanism
                const checkLoaded = setInterval(() => {
                    if (
                        typeof window.google !== 'undefined' &&
                        window.google.maps &&
                        typeof window.google.maps.importLibrary === 'function'
                    ) {
                        clearInterval(checkLoaded);
                        this.scriptLoaded = true;
                        resolve();
                    }
                }, 100);

                // Timeout with proper cleanup
                const timeout = setTimeout(() => {
                    clearInterval(checkLoaded);
                    if (!this.scriptLoaded) {
                        reject(
                            new Error(
                                'Google Maps API failed to load within 10 seconds'
                            )
                        );
                    }
                }, 10000);

                // Clean up timeout when resolved
                const originalResolve = resolve;
                resolve = () => {
                    clearTimeout(timeout);
                    originalResolve();
                };
            } catch (error) {
                reject(error);
            }
        });

        return this.loadPromise;
    }

    // Type-safe library loading with proper return types
    async loadLibrary<T extends LibraryName>(
        library: T
    ): Promise<LibraryMap[T]> {
        await this.load();

        if (!window.google?.maps?.importLibrary) {
            throw new Error('Google Maps API not properly loaded');
        }

        try {
            return (await window.google.maps.importLibrary(
                library
            )) as LibraryMap[T];
        } catch (error) {
            throw new Error(`Failed to load library '${library}': ${error}`);
        }
    }

    // Helper method to check if API is loaded
    isLoaded(): boolean {
        return (
            this.scriptLoaded &&
            typeof window.google !== 'undefined' &&
            !!window.google.maps
        );
    }

    // Helper method to get the Maps API instance
    getGoogleMaps(): GoogleMapsAPI['maps'] | null {
        return this.isLoaded() ? window.google.maps : null;
    }
}
